import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ConfirmationDialog } from './ui/confirmation-dialog';
import {
  Loader2, Save, RotateCcw, FileText, Eye, Info, Wand2, Code2, ChevronUp, ChevronDown,
} from 'lucide-react';

const FONT_OPTIONS = [
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica / Arial' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia / Times (serif)' },
  { value: '"Courier New", monospace', label: 'Courier (monospace)' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
];

// Extracted outside so React sees a stable component reference across re-renders
// (prevents input focus loss when design state updates on each keystroke).
function SectionToggle({ section, design, patchSection, children }) {
  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid={`design-block-${section}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">{SECTION_LABELS[section]}</Label>
        <Switch
          checked={design[section]?.show !== false}
          onCheckedChange={(v) => patchSection(section, { show: v })}
          data-testid={`design-${section}-show`}
        />
      </div>
      {design[section]?.show !== false && children}
    </div>
  );
}

const SECTION_LABELS = {
  header: 'Header & Logo',
  meta: 'Details grid',
  items: 'Docket times table',
  route_stops: 'Route Stops table',
  meter_readings: 'Meter Readings (SMU)',
  tolls: 'Tolls (no pricing)',
  notes: 'Notes',
  signature: 'Customer sign-off',
  terms: 'Terms & Conditions',
};

export const DocketPdfTemplateSettings = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('design');
  const [design, setDesign] = useState(null);
  const [defaultDesign, setDefaultDesign] = useState(null);
  const [metaCatalog, setMetaCatalog] = useState([]);
  const [html, setHtml] = useState('');
  const [terms, setTerms] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [fields, setFields] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [previewDocketId, setPreviewDocketId] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [ejectOpen, setEjectOpen] = useState(false);
  const htmlRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, cf, dm] = await Promise.all([
        api.get('/docket-pdf-template'),
        api.get('/docket-pdf-template/context-fields'),
        api.get('/docket-pdf-template/designer-meta'),
      ]);
      setMode('design');
      // Merge saved design over defaults so newly-added sections (e.g. tolls) are
      // always present for older saved templates.
      const def = dm.data.default_design || {};
      const saved = t.data.design || {};
      const merged = { ...def, ...saved };
      // Ensure the tolls section exists in the order (insert after items) without
      // discarding a user's custom ordering.
      let order = Array.isArray(saved.section_order) ? [...saved.section_order] : (def.section_order || Object.keys(SECTION_LABELS));
      if (!order.includes('tolls')) {
        const at = order.indexOf('items');
        order.splice(at >= 0 ? at + 1 : order.length, 0, 'tolls');
      }
      if (!order.includes('meter_readings')) {
        const at = order.indexOf('items');
        order.splice(at >= 0 ? at + 1 : order.length, 0, 'meter_readings');
      }
      if (!order.includes('route_stops')) {
        const at = order.indexOf('items');
        order.splice(at >= 0 ? at + 1 : order.length, 0, 'route_stops');
      }
      merged.section_order = order;
      // Normalise legacy show_company_details → new granular fields
      if (merged.header) {
        const legacy = merged.header.show_company_details;
        if (merged.header.show_company_name === undefined && legacy !== undefined) {
          merged.header.show_company_name = legacy;
        }
        if (merged.header.show_company_address === undefined && legacy !== undefined) {
          merged.header.show_company_address = legacy;
        }
      }
      setDesign(merged);
      setHtml(t.data.html_template || '');
      setTerms(t.data.terms_and_conditions || '');
      setIsDefault(!!t.data.is_default);
      setFields(cf.data?.fields || []);
      setDefaultDesign(dm.data.default_design);
      setMetaCatalog(dm.data.meta_fields || []);
      api.get('/docket-line-rules/sample-dockets').then(r => setDockets(r.data || [])).catch(() => {});
    } catch {
      toast.error('Failed to load PDF template');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // ---- design mutation helpers ----
  const patchSection = (section, updates) =>
    setDesign(d => ({ ...d, [section]: { ...d[section], ...updates } }));
  const patchStyles = (updates) =>
    setDesign(d => ({ ...d, styles: { ...d.styles, ...updates } }));

  const toggleMetaField = (key) =>
    setDesign(d => ({
      ...d,
      meta: {
        ...d.meta,
        fields: d.meta.fields.map(f => f.key === key ? { ...f, enabled: !f.enabled } : f),
      },
    }));
  const setMetaLabel = (key, label) =>
    setDesign(d => ({
      ...d,
      meta: { ...d.meta, fields: d.meta.fields.map(f => f.key === key ? { ...f, label } : f) },
    }));
  const moveMetaField = (idx, dir) =>
    setDesign(d => {
      const arr = [...d.meta.fields];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, meta: { ...d.meta, fields: arr } };
    });
  const moveSection = (idx, dir) =>
    setDesign(d => {
      const arr = [...(d.section_order || Object.keys(SECTION_LABELS))];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, section_order: arr };
    });

  const insertToken = (path) => {
    const simple = /^[a-z0-9_]+$/.test(path);
    const token = simple ? `{{ ${path} }}` : `{{ ${path.split(/[ [(]/)[0]} }}`;
    const el = htmlRef.current;
    if (!el) { setHtml(h => h + token); return; }
    const s = el.selectionStart ?? html.length;
    const e = el.selectionEnd ?? html.length;
    setHtml(html.slice(0, s) + token + html.slice(e));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + token.length; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = mode === 'design'
        ? { mode: 'design', design, terms_and_conditions: terms }
        : { mode: 'code', html_template: html, design, terms_and_conditions: terms };
      await api.put('/docket-pdf-template', payload);
      toast.success('Template saved');
      setIsDefault(false);
      if (previewDocketId) runPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    try {
      await api.delete('/docket-pdf-template');
      const d = await api.get('/docket-pdf-template/default');
      setDesign(d.data.design || defaultDesign);
      setHtml(d.data.html_template || '');
      setMode('design');
      setIsDefault(true);
      setResetOpen(false);
      setPreviewDocketId('');
      setPreviewUrl('');
      toast.success('Reverted to default template');
    } catch { toast.error('Failed to reset'); }
  };

  // Switch to Code mode: compile current design to HTML server-side (via preview path not needed;
  // we already keep html synced from load — but to be safe, request a fresh compile).
  const confirmEject = async () => {
    try {
      const res = await api.post('/docket-pdf-template/compile', { design });
      setHtml(res.data.html_template || html);
      setMode('code');
      setEjectOpen(false);
      toast.info('Switched to Code mode. Save to keep your hand-edited HTML.');
    } catch {
      toast.error('Could not switch to code mode');
    }
  };

  const runPreview = async () => {
    if (!previewDocketId) { toast.error('Choose a docket to preview'); return; }
    setPreviewing(true);
    try {
      const body = mode === 'design'
        ? { docket_id: previewDocketId, mode: 'design', design, terms_and_conditions: terms }
        : { docket_id: previewDocketId, mode: 'code', html_template: html, terms_and_conditions: terms };
      const res = await api.post('/docket-pdf-template/preview', body, { responseType: 'blob' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(pdfBlob));
    } catch (e) {
      let msg = 'Preview failed';
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setPreviewing(false); }
  };

  useEffect(() => { if (previewDocketId) runPreview(); /* eslint-disable-next-line */ }, [previewDocketId]);

  return (
    <Card className="card-industrial" data-testid="docket-pdf-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Docket PDF Template</CardTitle>
            <CardDescription>
              Design your docket PDF visually — brand colour, logo, section order &amp; visibility, and Terms &amp; Conditions. Preview on a real docket. {isDefault && <em>(currently using the built-in default)</em>}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="docket-pdf-reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset to default</Button>
            <Button className="btn-volt" onClick={handleSave} disabled={saving} data-testid="docket-pdf-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save</Button>
          </div>
        </div>
        {/* Mode switch removed — the docket PDF is rendered by the built-in
            engine driven entirely by the visual Design settings below. */}
      </CardHeader>
      <CardContent>
        {loading || !design ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: editor (design or code) */}
            <div className="space-y-4">
              {mode === 'design' ? (
                <div className="space-y-4" data-testid="docket-pdf-designer">
                  {/* Global styles */}
                  <div className="border rounded-lg p-3 space-y-3" data-testid="design-block-styles">
                    <Label className="text-sm font-semibold">Brand & typography</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Brand colour</Label>
                        <input type="color" value={design.styles.brand_color} onChange={(e) => patchStyles({ brand_color: e.target.value })}
                          className="h-9 w-full rounded border cursor-pointer bg-transparent" data-testid="design-brand-color" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Label colour</Label>
                        <input type="color" value={design.styles.accent_color} onChange={(e) => patchStyles({ accent_color: e.target.value })}
                          className="h-9 w-full rounded border cursor-pointer bg-transparent" data-testid="design-accent-color" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Font</Label>
                      <Select value={design.styles.font_family} onValueChange={(v) => patchStyles({ font_family: v })}>
                        <SelectTrigger className="h-9" data-testid="design-font-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Base font size — {design.styles.base_font_size}px</Label>
                      <Slider min={8} max={14} step={1} value={[design.styles.base_font_size]}
                        onValueChange={([v]) => patchStyles({ base_font_size: v })} data-testid="design-fontsize-slider" />
                    </div>
                  </div>

                  {/* Section order + toggles */}
                  <div className="border rounded-lg p-3 space-y-2" data-testid="design-section-order">
                    <Label className="text-sm font-semibold">Section order</Label>
                    <p className="text-[11px] text-muted-foreground">Reorder or hide sections. The footer always sits at the bottom of every page.</p>
                    {(design.section_order || Object.keys(SECTION_LABELS)).map((sec, idx, arr) => (
                      <div key={sec} className="flex items-center justify-between gap-2 rounded border px-2 py-1.5" data-testid={`section-order-row-${sec}`}>
                        <span className="text-sm">{SECTION_LABELS[sec] || sec}</span>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => moveSection(idx, -1)} data-testid={`section-up-${sec}`}><ChevronUp className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === arr.length - 1} onClick={() => moveSection(idx, 1)} data-testid={`section-down-${sec}`}><ChevronDown className="h-4 w-4" /></Button>
                          <Switch checked={design[sec]?.show !== false} onCheckedChange={(v) => patchSection(sec, { show: v })} data-testid={`section-show-${sec}`} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Header */}
                  <SectionToggle section="header" design={design} patchSection={patchSection}>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show company name</Label>
                      <Switch checked={design.header.show_company_name !== false} onCheckedChange={(v) => patchSection('header', { show_company_name: v })} data-testid="design-header-company-name" />
                    </div>
                    <p className="text-[11px] text-muted-foreground">Prints the company name right-aligned beside the logo.</p>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show company address</Label>
                      <Switch checked={design.header.show_company_address !== false} onCheckedChange={(v) => patchSection('header', { show_company_address: v })} data-testid="design-header-company-address" />
                    </div>
                    <p className="text-[11px] text-muted-foreground">Prints the business address &amp; phone below the company name.</p>
                    <div className="space-y-1">
                      <Label className="text-xs">Top-right title</Label>
                      <Input className="h-8" value={design.header.title || ''} onChange={(e) => patchSection('header', { title: e.target.value })} data-testid="design-header-title" />
                    </div>
                  </SectionToggle>

                  {/* Meta grid */}
                  <SectionToggle section="meta" design={design} patchSection={patchSection}>
                    <div className="space-y-1">
                      <Label className="text-xs">Columns</Label>
                      <Select value={String(design.meta.columns)} onValueChange={(v) => patchSection('meta', { columns: Number(v) })}>
                        <SelectTrigger className="h-8" data-testid="design-meta-columns"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="1">1</SelectItem><SelectItem value="2">2</SelectItem><SelectItem value="3">3</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <Label className="text-xs">Fields (toggle, rename, reorder)</Label>
                    <div className="space-y-1">
                      {design.meta.fields.map((f, idx) => (
                        <div key={f.key} className="flex items-center gap-2 rounded border px-2 py-1" data-testid={`meta-field-row-${f.key}`}>
                          <Switch checked={!!f.enabled} onCheckedChange={() => toggleMetaField(f.key)} data-testid={`meta-field-toggle-${f.key}`} />
                          <Input className="h-7 text-xs flex-1" value={f.label} onChange={(e) => setMetaLabel(f.key, e.target.value)} data-testid={`meta-field-label-${f.key}`} />
                          <span className="text-[10px] text-muted-foreground w-24 truncate">{(metaCatalog.find(m => m.key === f.key) || {}).label || f.key}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" disabled={idx === 0} onClick={() => moveMetaField(idx, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" disabled={idx === design.meta.fields.length - 1} onClick={() => moveMetaField(idx, 1)}><ChevronDown className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  </SectionToggle>

                  {/* Items */}
                  <SectionToggle section="items" design={design} patchSection={patchSection}>
                    <div className="space-y-1">
                      <Label className="text-xs">Heading</Label>
                      <Input className="h-8" value={design.items.heading || ''} onChange={(e) => patchSection('items', { heading: e.target.value })} data-testid="design-items-heading" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Header background</Label>
                        <input type="color" value={design.items.header_bg} onChange={(e) => patchSection('items', { header_bg: e.target.value })} className="h-8 w-full rounded border cursor-pointer bg-transparent" data-testid="design-items-headerbg" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Header text</Label>
                        <input type="color" value={design.items.header_text_color} onChange={(e) => patchSection('items', { header_text_color: e.target.value })} className="h-8 w-full rounded border cursor-pointer bg-transparent" data-testid="design-items-headertext" />
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Resource, Start Time, Finish Time and Hours are always shown. Dockets with depot times also show Depart Depot / Arrive Site / Depart Site / Arrive Depot.</p>
                    <Label className="text-xs">Columns</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[['show_break', 'Break'], ['show_travel', 'Travel']].map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                          <Switch checked={design.items[k] !== false} onCheckedChange={(v) => patchSection('items', { [k]: v })} data-testid={`design-items-${k}`} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </SectionToggle>

                  {/* Route Stops column toggles */}
                  <SectionToggle section="route_stops" design={design} patchSection={patchSection}>
                    <p className="text-[11px] text-muted-foreground">Choose which columns appear on the Route Stops table. # / Location / Arrived are always shown.</p>
                    {[
                      { key: 'type', label: 'Type' },
                      { key: 'eta', label: 'ETA' },
                      { key: 'completed', label: 'Completed' },
                      { key: 'notes', label: 'Notes' },
                      { key: 'works', label: 'Works' },
                    ].map(col => (
                      <label key={col.key} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Switch
                          checked={(design.route_stops?.columns || {})[col.key] !== false}
                          onCheckedChange={(v) => patchSection('route_stops', { columns: { ...(design.route_stops?.columns || {}), [col.key]: v } })}
                          data-testid={`design-route-stops-col-${col.key}`}
                        />
                        {col.label}
                      </label>
                    ))}
                  </SectionToggle>

                  {/* Notes / Tolls / Signature / Terms headings */}
                  {['notes', 'tolls', 'signature', 'terms'].map(sec => (
                    <SectionToggle key={sec} section={sec} design={design} patchSection={patchSection}>
                      <div className="space-y-1">
                        <Label className="text-xs">Heading</Label>
                        <Input className="h-8" value={design[sec]?.heading || ''} onChange={(e) => patchSection(sec, { heading: e.target.value })} data-testid={`design-${sec}-heading`} />
                      </div>
                    </SectionToggle>
                  ))}

                  {/* Terms text + footer toggle */}
                  <div className="border rounded-lg p-3 space-y-2">
                    <Label className="text-xs">Terms &amp; Conditions text</Label>
                    <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={4} placeholder="Shown in the Terms & Conditions section" data-testid="docket-pdf-terms-input" />
                    <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                      <Switch checked={design.footer?.show !== false} onCheckedChange={(v) => patchSection('footer', { show: v })} data-testid="design-footer-show" />
                      Show page footer (company · docket # · generated date)
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-4" data-testid="docket-pdf-code">
                  <div>
                    <Label>Template HTML</Label>
                    <Textarea ref={htmlRef} value={html} onChange={(e) => setHtml(e.target.value)} rows={22}
                      className="font-mono text-xs" spellCheck={false} data-testid="docket-pdf-html-input" />
                  </div>
                  <div>
                    <Label>Terms &amp; Conditions</Label>
                    <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={4}
                      placeholder="Injected via {{ terms_and_conditions }} in your template" data-testid="docket-pdf-terms-input-code" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Info className="h-4 w-4" /> Available tokens</Label>
                    <p className="text-[11px] text-muted-foreground">Click to insert at your cursor. Lists (with <code>[]</code>) are looped with <code>{'{% for i in items %}'}</code>.</p>
                    <div className="border rounded-md max-h-[300px] overflow-y-auto divide-y">
                      {fields.map(f => (
                        <button key={f.path} type="button" onClick={() => insertToken(f.path)}
                          className="w-full text-left p-2 hover:bg-muted transition-colors" data-testid={`docket-pdf-token-${f.path.split(/[ [(]/)[0]}`}>
                          <code className="text-[11px] text-indigo-600 dark:text-indigo-300 break-all">{f.path}</code>
                          <div className="text-[11px] text-muted-foreground">{f.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right: live preview — sticky so it follows the user while scrolling settings */}
            <div className="space-y-2 lg:sticky lg:top-20 lg:self-start">
              <Label className="flex items-center gap-2"><Eye className="h-4 w-4" /> Live preview</Label>
              <div className="flex gap-2">
                <Select value={previewDocketId} onValueChange={setPreviewDocketId}>
                  <SelectTrigger className="flex-1" data-testid="docket-pdf-preview-docket-select"><SelectValue placeholder="Choose a docket…" /></SelectTrigger>
                  <SelectContent>
                    {dockets.some(d => d.is_demo) && (
                      <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Sample Data</div>
                    )}
                    {dockets.filter(d => d.is_demo).map(d => (
                      <SelectItem key={d.id} value={d.id} data-testid={`docket-pdf-demo-${d.id}`}>
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                          {d.label}
                        </span>
                      </SelectItem>
                    ))}
                    {dockets.some(d => !d.is_demo) && (
                      <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-t mt-1 pt-1.5">Your Dockets</div>
                    )}
                    {dockets.filter(d => !d.is_demo).map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={runPreview} disabled={previewing || !previewDocketId} data-testid="docket-pdf-preview-btn">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
              {previewUrl
                ? <iframe title="PDF preview" src={previewUrl} type="application/pdf" className="w-full h-[calc(100vh-10rem)] border rounded-md bg-white" data-testid="docket-pdf-preview-frame" />
                : <div className="w-full h-[calc(100vh-10rem)] border rounded-md bg-muted/30 flex items-center justify-center text-sm text-muted-foreground" data-testid="docket-pdf-preview-empty">Choose a docket to see a live preview</div>}
            </div>
          </div>
        )}
      </CardContent>

      <ConfirmationDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Reset PDF template"
        description="Discard your custom template and revert to the built-in default designer? Your Terms & Conditions text is kept."
        confirmText="Reset"
        variant="destructive"
        onConfirm={handleReset}
        testId="docket-pdf-reset-dialog"
      />

      <ConfirmationDialog
        open={ejectOpen}
        onOpenChange={setEjectOpen}
        title="Switch to Code mode?"
        description="Code mode gives you full HTML/CSS control, but once you hand-edit and save, the visual designer no longer drives this template. You can always Reset to default to return to the designer."
        confirmText="Switch to Code"
        onConfirm={confirmEject}
        testId="docket-pdf-eject-dialog"
      />
    </Card>
  );
};

export default DocketPdfTemplateSettings;
